import { formatPriceCompact } from '@/lib/format/money'
import { zonedDateKey } from '@/lib/format/date'
import { HISTORY_EVENT_LABEL, newestRailCut } from '@/lib/listing/publish-history-rail'

/**
 * One published price/status timeline for a listing detail page.
 *
 * `listing_history` is the Spark full-history table. Recent live listings
 * often have zero rows there until strict verify. Delta sync already writes
 * `status_history` and `price_history`. A page that reads only
 * `listing_history` hides the timeline the fleet case pack requires.
 *
 * Founding cases (fleet:public-ux:listing-detail 2026-08-17):
 *   61055 Borden (220225742) — listed Jul 22, empty listing_history
 *   61579 Rockway (220226183) — Coming Soon → Active → Pending
 *   61345 Mountain Breezes (220226708) — Coming Soon → Active
 *
 * The published timeline is the merge. Do not invent a price. Listed uses
 * OnMarketDate plus the first price_history old_price dated on or after
 * that day (the original ask of this cycle). Current ListPrice is the
 * fallback only when no later cut exists. Founding case: 909 NW Delaware
 * (220222734) listed Jun 4 at $1,195,000, not the live $999,000 ask.
 *
 * A status event dated before the published listed date is a prior cycle.
 * Foley (220221409) was Active / 2 DOM with OnMarketDate Aug 16 after a
 * Pending Aug 2 fall-through. Publishing that pending before listed
 * contradicts the live listing. Price changes on the same ListingKey stay.
 * Founding case: 2590 Foley (220221409) fleet:07a696dd6362d063f1f6cc1980f3e22f.
 *
 * Buyer-facing copy never prints a raw MLS field dump
 * (`ListPrice: 14900000.00 → 11900000.00`). Price and the dollar delta
 * already sit on the row. Founding case: 65255 Swalley (220207865)
 * fleet:7e278bfeb28c9806649154eeb32c5567.
 */

export type PublishedListingHistoryEvent = {
  event: string
  event_date: string
  price?: number | null
  price_change?: number | null
  description?: string | null
}

export type ListingHistorySourceRow = {
  event?: string | null
  event_date?: string | null
  price?: number | null
  price_change?: number | null
  description?: string | null
}

export type StatusHistorySourceRow = {
  old_status?: string | null
  new_status?: string | null
  changed_at?: string | null
}

export type PriceHistorySourceRow = {
  old_price?: number | null
  new_price?: number | null
  changed_at?: string | null
  change_pct?: number | null
}

function dateOnly(iso: string | null | undefined): string | null {
  if (!iso?.trim()) return null
  const trimmed = iso.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  return zonedDateKey(trimmed) || null
}

function normalizeEvent(raw: string | null | undefined): string {
  return (raw ?? '').toLowerCase().replace(/[\s_-]+/g, '')
}

function asPositivePrice(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null
  return Math.round(value)
}

/**
 * Withhold MLS change-log dumps. Keep human prose. Do not rewrite
 * `ListPrice: 14900000.00 → 11900000.00` into a second price line.
 */
export function publishListingHistoryDescription(
  description: string | null | undefined,
): string | null {
  const raw = (description ?? '').trim()
  if (!raw) return null
  if (/^[A-Z][A-Za-z0-9]*:\s*[\d.,]+/.test(raw)) return null
  if (/\bListPrice:\s*[\d.,]+/.test(raw)) return null
  return raw
}

/** Dollar delta for the history rail. `$3.0M down`, never `3,000,000 down`. */
export function publishListingHistoryDeltaLabel(
  amount: number | null | undefined,
  direction: 'down' | 'up',
): string | null {
  const n = asPositivePrice(amount)
  if (n == null) return null
  return `${formatPriceCompact(n)} ${direction}`
}

const STATUS_EVENT: Record<string, string> = {
  pending: 'pending',
  statuspending: 'pending',
  activeundercontract: 'pending',
  contingent: 'contingent',
  statuscontingent: 'contingent',
  active: 'active',
  statusactive: 'active',
  backonmarket: 'backonmarket',
  closed: 'sold',
  sold: 'sold',
  statusclosed: 'sold',
  expired: 'expired',
  statusexpired: 'expired',
  canceled: 'canceled',
  cancelled: 'canceled',
  statuscanceled: 'canceled',
  withdrawn: 'withdrawn',
  statuswithdrawn: 'withdrawn',
}

function statusEvent(newStatus: string | null | undefined): string | null {
  const key = normalizeEvent(newStatus)
  if (!key || key === 'comingsoon') return null
  return STATUS_EVENT[key] ?? null
}

const PUBLISHED_STATUS_EVENTS = new Set<string>(Object.values(STATUS_EVENT))

function isPublishedStatusEvent(event: string): boolean {
  const key = normalizeEvent(event)
  if (!key) return false
  return PUBLISHED_STATUS_EVENTS.has(key) || STATUS_EVENT[key] != null
}

const PRICE_EVENT_KEYS = new Set(['pricechange', 'pricedrop', 'priceincrease'])
const PRICE_ANCHOR_KEYS = new Set(['listed', 'newlisting', ...PRICE_EVENT_KEYS])

function isPriceEventKey(event: string | null | undefined): boolean {
  return PRICE_EVENT_KEYS.has(normalizeEvent(event))
}

/** "Price drop" / "Price increase" say more than "Price change" about one event. */
function priceEventRank(event: string | null | undefined): number {
  const key = normalizeEvent(event)
  if (key === 'pricedrop' || key === 'priceincrease') return 2
  if (key === 'pricechange') return 1
  return 0
}

/**
 * One price event is one key: the Pacific calendar day and the price it set.
 * W26 (2026-10-21), 3335 NW Fairway Heights: every cut printed twice, once as
 * "Price change" (price_history) and once as "Price drop" (the Spark change
 * log), because the event name was part of the key.
 */
function eventKey(event: string, date: string, price: number | null): string {
  if (isPriceEventKey(event)) return `price|${date}|${price ?? ''}`
  return `${normalizeEvent(event)}|${date}|${price ?? ''}`
}

/**
 * The ListPrice move a Spark FieldChange carries, as sparkHistoryItemToRow
 * writes it into `description` ("ListPrice: 1645000.00 → 1599000.00").
 */
function listPriceFieldChange(
  description: string | null | undefined,
): { from: number; to: number } | null {
  const m = /\bListPrice:\s*([\d.,]+)\s*(?:→|->)\s*([\d.,]+)/.exec(description ?? '')
  if (!m) return null
  const from = asPositivePrice(Number(m[1]!.replace(/,/g, '')))
  const to = asPositivePrice(Number(m[2]!.replace(/,/g, '')))
  return from != null && to != null ? { from, to } : null
}

/**
 * A change-log row that moved the ask (FieldChange, or any event the rail has
 * no label for) published as the price event it is, so it dedupes against the
 * price_history row for the same move instead of printing beside it.
 */
function listingHistoryPriceAction(
  row: ListingHistorySourceRow,
): { event: 'pricedrop' | 'priceincrease'; price: number; price_change: number | null } | null {
  const key = normalizeEvent(row.event)
  if (!key || HISTORY_EVENT_LABEL[key]) return null
  const move = listPriceFieldChange(row.description)
  const price = asPositivePrice(row.price) ?? move?.to ?? null
  const raw = row.price_change != null && Number.isFinite(row.price_change) && row.price_change !== 0 ? row.price_change : null
  // Spark's PriceChange on a FieldChange is a FRACTION of the ask (-0.015 on
  // Fairway Heights' $25,000 cut), not dollars. Its sign names the direction;
  // the dollars come from the prices (the rail measures them) or the move.
  const dollars = raw != null && Math.abs(raw) >= 1 ? Math.round(raw) : move ? move.to - move.from : null
  const sign = Math.sign(dollars ?? raw ?? 0)
  if (price == null || sign === 0) return null
  return { event: sign < 0 ? 'pricedrop' : 'priceincrease', price, price_change: dollars || null }
}

/**
 * Original ask of the current cycle: earliest price_history old_price on or
 * after the listed date. Prior-cycle cuts (Foley) stay off this listed row.
 */
function listedAskFromPriceHistory(
  priceHistory: ReadonlyArray<PriceHistorySourceRow> | undefined,
  listedDate: string | null,
  originalListPrice: number | null,
  listPrice: number | null,
): number | null {
  if (!listedDate) return listPrice
  const later = (priceHistory ?? [])
    .map((row) => ({
      date: dateOnly(row.changed_at),
      old: asPositivePrice(row.old_price),
    }))
    .filter((row): row is { date: string; old: number } => row.date != null && row.old != null && row.date >= listedDate)
    .sort((a, b) => a.date.localeCompare(b.date))
  return later[0]?.old ?? originalListPrice ?? listPrice
}

/**
 * Most recent cut on the published rail. Face is last drop ($76K),
 * not original-minus-ask.
 */
export function publishListingLastDrop(
  rows: ReadonlyArray<{
    event?: string | null
    event_date?: string | null
    price?: number | null
    price_change?: number | null
  }>,
): { amount: number; label: string } | null {
  // A RISE NEWER THAN THE CUT ENDS THE SEARCH (2026-09-11), and the cut is the
  // history rail's own row and delta (W6, 2026-10-20): the same newestRailCut
  // publishListingDropMark reads, so the badge, the mark and the "$Y down" on
  // the rail row are one figure. Newest-first, the first real price action
  // decides; a rise means no cut is current.
  const row = newestRailCut(rows)
  if (!row || row.delta == null) return null
  const amount = Math.round(Math.abs(row.delta))
  return { amount, label: `Price drop ${formatPriceCompact(amount)}` }
}

export function publishListingHistory(input: {
  listingHistory?: ReadonlyArray<ListingHistorySourceRow>
  statusHistory?: ReadonlyArray<StatusHistorySourceRow>
  priceHistory?: ReadonlyArray<PriceHistorySourceRow>
  onMarketDate?: string | null
  listPrice?: number | null
  originalListPrice?: number | null
}): PublishedListingHistoryEvent[] {
  const out: PublishedListingHistoryEvent[] = []
  const seen = new Map<string, number>()

  const push = (row: PublishedListingHistoryEvent) => {
    const date = dateOnly(row.event_date)
    if (!date) return
    const price = asPositivePrice(row.price)
    const key = eventKey(row.event, date, price)
    const at = seen.get(key)
    if (at != null) {
      // Same day, same resulting price: one event. Keep the more specific
      // label and the first stored change; the rail measures the delta from
      // the prices anyway.
      const kept = out[at]!
      if (priceEventRank(row.event) > priceEventRank(kept.event)) kept.event = row.event
      if (kept.price_change == null && row.price_change != null) kept.price_change = row.price_change
      return
    }
    seen.set(key, out.length)
    out.push({
      event: row.event,
      event_date: date,
      price,
      price_change: row.price_change ?? null,
      description: publishListingHistoryDescription(row.description),
    })
  }

  for (const row of input.listingHistory ?? []) {
    if (!row.event?.trim()) continue
    const priceAction = listingHistoryPriceAction(row)
    if (priceAction) {
      push({ ...priceAction, event_date: row.event_date ?? '', description: row.description })
      continue
    }
    const event = normalizeEvent(row.event)
    const isPriceEvent = event === 'pricechange' || event === 'pricedrop' || event === 'priceincrease'
    if (isPriceEvent && row.price_change === 0) continue
    push({
      event: row.event,
      event_date: row.event_date ?? '',
      price: row.price,
      price_change: row.price_change,
      description: row.description,
    })
  }

  const listedDate = dateOnly(input.onMarketDate)
  const listPrice = asPositivePrice(input.listPrice)
  const listedAsk = listedAskFromPriceHistory(
    input.priceHistory,
    listedDate,
    asPositivePrice(input.originalListPrice),
    listPrice,
  )
  if (listedDate) {
    const alreadyListed = [...seen.keys()].some((k) => k.startsWith('listed|') || k.startsWith('newlisting|'))
    if (!alreadyListed) {
      push({
        event: 'listed',
        event_date: listedDate,
        price: listedAsk,
      })
    }
  }

  // The ask in effect at an instant: the newest price_history step at or before
  // it, else this cycle's listed ask. W6 (2026-10-20): a status row used to
  // carry TODAY's ListPrice, so a Pending row dated before a cut printed the
  // post-cut price and the rail read a "$46K down" on the Pending row.
  const priceSteps = (input.priceHistory ?? [])
    .map((row) => ({ at: Date.parse(row.changed_at ?? ''), price: asPositivePrice(row.new_price) }))
    .filter((step): step is { at: number; price: number } => Number.isFinite(step.at) && step.price != null)
    .sort((a, b) => a.at - b.at)
  const askInEffect = (iso: string | null | undefined): number | null => {
    const at = Date.parse(iso ?? '')
    let price: number | null = null
    if (Number.isFinite(at)) {
      for (const step of priceSteps) if (step.at <= at) price = step.price
    }
    return price ?? listedAsk ?? listPrice
  }

  for (const row of input.statusHistory ?? []) {
    const event = statusEvent(row.new_status)
    const date = dateOnly(row.changed_at)
    if (!event || !date) continue
    const oldKey = normalizeEvent(row.old_status)
    if (oldKey === 'comingsoon' && event === 'active' && date === listedDate) continue
    if (event === 'active' && date === listedDate) continue
    push({
      event,
      event_date: date,
      price: askInEffect(row.changed_at),
    })
  }

  for (const row of input.priceHistory ?? []) {
    const date = dateOnly(row.changed_at)
    const newPrice = asPositivePrice(row.new_price)
    const oldPrice = asPositivePrice(row.old_price)
    if (!date || newPrice == null) continue
    if (oldPrice != null && newPrice === oldPrice) continue
    push({
      event: 'pricechange',
      event_date: date,
      price: newPrice,
      price_change: oldPrice != null ? newPrice - oldPrice : null,
    })
  }

  const listedCutoff =
    listedDate ??
    out
      .filter((row) => {
        const key = normalizeEvent(row.event)
        return key === 'listed' || key === 'newlisting'
      })
      .map((row) => row.event_date)
      .sort()
      .at(-1) ?? null

  return out
    .filter((row) => {
      if (!listedCutoff) return true
      if (!isPublishedStatusEvent(row.event)) return true
      return row.event_date >= listedCutoff
    })
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .filter((row, i, rows) => {
      const key = normalizeEvent(row.event)
      if (key !== 'pricechange' && key !== 'pricedrop' && key !== 'priceincrease') return true
      const prev = [...rows.slice(0, i)].reverse().find((r) => r.price != null)
      if (prev && prev.price === row.price && (row.price_change == null || row.price_change === 0)) {
        return false
      }
      return true
    })
}

/**
 * One row per day for price changes: same-day changes collapse into their net.
 *
 * W6 (Muse audit 2026-10-20). 3335 NW Fairway Heights carried a -$50,000 and a
 * +$4,000 change on one day: the rail printed both ("$50K down", then a "price
 * increase" of $4K) while the banner over it printed the day's net, $46K. The
 * day's net is the fact (the ask went from $1,645,000 to $1,599,000 that day);
 * a correction keyed in an hour later is not a price increase a buyer saw.
 *
 * Net = the day's last price minus the ask before the day (the nearest earlier
 * listed or price row). A day whose changes cancel prints nothing. When either
 * end cannot be read off the rows, the day is left exactly as published.
 * `currentPrice` (the live ListPrice) names the day's last price on the newest
 * price day when one of that day's rows carries it, because rows from two
 * sources lose their order inside a day.
 *
 * W26 (2026-10-21), the same home on Oct 7: three rows for one day ("$29K
 * down", "$4K down", "$25K down") that cannot all be true. The day is one
 * row: the prior day's last ask to the day's final ask.
 *
 * Rows must be oldest first (publishListingHistory's order).
 */
export function collapseSameDayPriceChanges<
  T extends { event?: string | null; event_date?: string | null; price?: number | null; price_change?: number | null },
>(rows: ReadonlyArray<T>, options: { currentPrice?: number | null } = {}): T[] {
  const isPrice = (row: T) => PRICE_EVENT_KEYS.has(normalizeEvent(row.event))
  const byDay = new Map<string, number[]>()
  rows.forEach((row, i) => {
    if (!isPrice(row) || !row.event_date) return
    const list = byDay.get(row.event_date) ?? []
    list.push(i)
    byDay.set(row.event_date, list)
  })
  const priceDays = [...byDay.keys()].sort()
  const newestPriceDay = priceDays.at(-1) ?? null
  const currentPrice = asPositivePrice(options.currentPrice)
  const drop = new Set<number>()
  const replace = new Map<number, T>()

  for (const [day, idxs] of byDay) {
    if (idxs.length < 2) continue
    const group = new Set(idxs)
    let before: number | null = null
    for (let i = idxs[0]! - 1; i >= 0; i--) {
      const row = rows[i]!
      if (group.has(i) || !PRICE_ANCHOR_KEYS.has(normalizeEvent(row.event))) continue
      const price = asPositivePrice(row.price)
      if (price != null) {
        before = price
        break
      }
    }
    if (before == null) {
      const first = rows[idxs[0]!]!
      const price = asPositivePrice(first.price)
      if (price != null && first.price_change != null && Number.isFinite(first.price_change)) {
        before = asPositivePrice(price - first.price_change)
      }
    }
    let lastIdx = [...idxs].reverse().find((i) => asPositivePrice(rows[i]!.price) != null) ?? null
    if (day === newestPriceDay && currentPrice != null) {
      const carrying = [...idxs].reverse().find((i) => asPositivePrice(rows[i]!.price) === currentPrice)
      if (carrying != null) lastIdx = carrying
    }
    const after = lastIdx != null ? asPositivePrice(rows[lastIdx]!.price) : null
    if (before == null || after == null || lastIdx == null) continue
    const net = after - before
    for (const i of idxs) drop.add(i)
    if (net === 0) continue
    const at = idxs.at(-1)!
    // W26: a day the change log called a drop or an increase keeps that word,
    // named by the day's net, not by whichever row happened to come last.
    const specific = idxs.some((i) => priceEventRank(rows[i]!.event) === 2)
    const event = specific ? (net < 0 ? 'pricedrop' : 'priceincrease') : 'pricechange'
    replace.set(at, { ...rows[lastIdx]!, event, price: after, price_change: net })
    drop.delete(at)
  }

  return rows.flatMap((row, i) => {
    if (replace.has(i)) return [replace.get(i)!]
    return drop.has(i) ? [] : [row]
  })
}

/** The price a SALE row publishes (SITE-21). Lives beside the rail it prices. */
export { publishHistoryRowPrice } from '@/lib/listing/publish-history-rail'
